import {
  AtSymbolIcon,
  BookOpenIcon,
  PhoneIcon,
  ShoppingCartIcon
} from "@heroicons/react/outline";
import { ArrowRightIcon, ChatAlt2Icon, ChatIcon } from "@heroicons/react/solid";
import { get, isEmpty } from "lodash";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import DiscordIcon from "../../../components/icons/DiscordIcon";
import FacebookIcon from "../../../components/icons/FacebookIcon";
import GithubIcon from "../../../components/icons/GithubIcon";
import InstagramIcon from "../../../components/icons/InstagramIcon";
import LinkedinIcon from "../../../components/icons/LinkedinIcon";
import RedditIcon from "../../../components/icons/RedditIcon";
import TelegramIcon from "../../../components/icons/TelegramIcon";
import TwitterIcon from "../../../components/icons/TwitterIcon";
import YoutubeIcon from "../../../components/icons/YoutubeIcon";
import { hotspotMakerMetadata } from "../../../context/hotspotMakers/HotspotMakersProvider";
import classNames from "../../../utils/classNames";

const MakerLinksTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const links = useMemo(
    () =>
      linkConfigs.filter(
        (linkConfig) =>
          !!get(hotspotMakerMetadata, [props.match.params.id, linkConfig.key])
      ),
    [props.match.params.id]
  );

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {isEmpty(links) ? (
        "No links are available for this maker."
      ) : (
        <div className="rounded-lg overflow-hidden shadow divide-y divide-gray-200 dark:divide-gray-700 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px">
          {links.map(
            (
              { title, icon: Icon, iconBackground, iconForeground, key },
              index
            ) => (
              <div
                key={key}
                className={classNames(
                  index === 0 &&
                    "rounded-tl-lg rounded-tr-lg sm:rounded-tr-none",
                  index === 1 && "sm:rounded-tr-lg",
                  index === linkConfigs.length - 2 && "sm:rounded-bl-lg",
                  index === linkConfigs.length - 1 &&
                    "rounded-bl-lg rounded-br-lg sm:rounded-bl-none",
                  "relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-hotspotty-400 dark:bg-gray-800 flex items-center"
                )}
              >
                <div
                  className={classNames(
                    iconBackground,
                    iconForeground,
                    "rounded-lg inline-flex p-3 ring-4 ring-white"
                  )}
                >
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </div>

                <h3 className="flex-1 ml-6 text-lg font-medium dark:text-gray-200">
                  <a
                    href={get(hotspotMakerMetadata, [
                      props.match.params.id,
                      key
                    ])}
                    className="focus:outline-none group-hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {/* Extend touch target to entire panel */}
                    <span className="absolute inset-0" aria-hidden="true" />
                    {title}
                  </a>
                </h3>

                <ArrowRightIcon className="h-6 w-6 pointer-events-none text-gray-300 group-hover:text-gray-400 dark:group-hover:text-gray-50" />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MakerLinksTab;

//
// Utils
//

const linkConfigs = [
  {
    key: "purchaseLink",
    title: "Purchase hotspots",
    icon: ShoppingCartIcon,
    iconForeground: "text-green-700",
    iconBackground: "bg-green-50"
  },
  {
    key: "communityLink",
    title: "Community",
    icon: ChatAlt2Icon,
    iconForeground: "text-pink-700",
    iconBackground: "bg-pink-50"
  },
  {
    key: "discordLink",
    title: "Discord",
    icon: DiscordIcon,
    iconForeground: "text-pink-700",
    iconBackground: "bg-pink-50"
  },
  {
    key: "blogLink",
    title: "Blog",
    icon: BookOpenIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "twitterLink",
    title: "Twitter",
    icon: TwitterIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "instagramLink",
    title: "Instagram",
    icon: InstagramIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "facebookLink",
    title: "Facebook",
    icon: FacebookIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "redditLink",
    title: "Reddit",
    icon: RedditIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "telegramLink",
    title: "Telegram",
    icon: TelegramIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "youtubeLink",
    title: "Youtube",
    icon: YoutubeIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "linkedinLink",
    title: "Linkedin",
    icon: LinkedinIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "githubLink",
    title: "Github",
    icon: GithubIcon,
    iconForeground: "text-hotspotty-700",
    iconBackground: "bg-hotspotty-50"
  },
  {
    key: "supportLink",
    title: "Support",
    icon: ChatIcon,
    iconForeground: "text-yellow-700",
    iconBackground: "bg-yellow-50"
  },
  {
    key: "emailSupportLink",
    title: "Email support",
    icon: AtSymbolIcon,
    iconForeground: "text-yellow-700",
    iconBackground: "bg-yellow-50"
  },
  {
    key: "emailSalesLink",
    title: "Email sales",
    icon: AtSymbolIcon,
    iconForeground: "text-yellow-700",
    iconBackground: "bg-yellow-50"
  },
  {
    key: "phoneLink",
    title: "Phone",
    icon: PhoneIcon,
    iconForeground: "text-yellow-700",
    iconBackground: "bg-yellow-50"
  }
];
